// Clients → Top clients (Client Accounts plan, section 3). Every named client ranked by spend over every channel and tender,
// counter cash, Zelle and crypto included. Stubs (unnamed Shopify customers), staff and house accounts are left out; the
// progress line counts the stubs still to be named. Assigning an advisor is signed with an owner ID and audited.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '../../ReportsApp'
import { reportsAction, useReport } from '../../api'
import { int, money } from '../../format'
import { Button, Tile } from '../../ui'
import { useOwnerSigner } from '../owner-edit'
import { Avatar, type ClientRow, type Advisor, Dot, Seg, SplitBar, SplitKey, TierChip, clientsCsv, dateY, daysLabel } from './clients-ui'

export interface TopData {
  config: { on: boolean; top_cents: number; top_purchases: number; core_cents: number; months: number; lapsed_days: number }
  tiles: { with_purchase: number; clients: number; top: number; core: number; no_client_pct: number; no_client: number; no_client_n: number; lapsed_top_core: number; no_advisor_top_core: number }
  progress: { shopify: number; named: number; to_fill: number }
  advisors: Advisor[]
  clients: ClientRow[]
}
type Filter = 'buyers' | 'top' | 'core' | 'buyer' | 'prospect' | 'lapsed' | 'no_advisor' | 'no_consent' | 'all'

export function AdvisorPick({ row, advisors, onSaved, signer }: { row: { id: string; advisor: string | null; name: string }; advisors: Advisor[]; onSaved: () => void; signer: ReturnType<typeof useOwnerSigner> }) {
  const [err, setErr] = useState('')
  const set = (v: string) => signer.signed(async () => {
    setErr('')
    try { await reportsAction('client_advisor', { employee_id: signer.idRef.current, customer_id: row.id, advisor: v || null }); onSaved() } catch (e) { setErr((e as Error).message) }
  })
  return (
    <span className="cl-advpick" onClick={(e) => e.stopPropagation()}>
      <select className="cl-advpick__sel" value={row.advisor ?? ''} onChange={(e) => set(e.target.value)} aria-label={`Advisor for ${row.name}`}>
        <option value="">No advisor</option>
        {advisors.map((a) => <option key={a.employee_id} value={a.employee_id}>{a.name}</option>)}
      </select>
      <Avatar name={advisors.find((a) => a.employee_id === row.advisor)?.name ?? null} />
      {err && <span className="rp-tone-red cl-small">{err}</span>}
    </span>
  )
}

export function TopClients() {
  const { data: d, error, loading, refresh } = useReport<TopData>('clients_top')
  const navigate = useNavigate()
  const signer = useOwnerSigner()
  const [range, setRange] = useState<'life' | 'l12'>('life')
  const [filter, setFilter] = useState<Filter>('buyers')
  const [advisor, setAdvisor] = useState('')
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(100)
  const rows = useMemo(() => {
    let r = d?.clients ?? []
    if (filter === 'buyers') r = r.filter((c) => c.purchases > 0)
    else if (filter === 'lapsed') r = r.filter((c) => (c.tier === 'top' || c.tier === 'core') && c.lapsed)
    else if (filter === 'no_advisor') r = r.filter((c) => (c.tier === 'top' || c.tier === 'core') && !c.advisor)
    else if (filter === 'no_consent') r = r.filter((c) => c.purchases > 0 && !c.email_ok)
    else if (filter !== 'all') r = r.filter((c) => c.tier === filter)
    if (advisor) r = r.filter((c) => (advisor === 'none' ? !c.advisor : c.advisor === advisor))
    const s = q.trim().toLowerCase(), digits = s.replace(/\D/g, '')
    if (s) r = r.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || c.city.toLowerCase().includes(s) || (digits.length >= 4 && c.phone.replace(/\D/g, '').includes(digits)))
    return [...r].sort((a, b) => (range === 'life' ? b.life - a.life || b.l12 - a.l12 : b.l12 - a.l12 || b.life - a.life))
  }, [d, filter, advisor, q, range])
  const t = d?.tiles, p = d?.progress, cfg = d?.config
  const count = (f: Filter) => {
    const c = d?.clients ?? []
    switch (f) {
      case 'buyers': return c.filter((x) => x.purchases > 0).length
      case 'lapsed': return t?.lapsed_top_core ?? 0
      case 'no_advisor': return t?.no_advisor_top_core ?? 0
      case 'no_consent': return c.filter((x) => x.purchases > 0 && !x.email_ok).length
      case 'all': return c.length
      default: return c.filter((x) => x.tier === f).length
    }
  }
  const FILTERS: [Filter, string][] = [['buyers', 'Bought'], ['top', 'Top'], ['core', 'Core'], ['buyer', 'Buyer'], ['prospect', 'Prospect'], ['lapsed', 'Lapsed Top and Core'], ['no_advisor', 'No advisor'], ['no_consent', 'No email consent'], ['all', 'Everyone']]
  const cols = '36px minmax(180px, 1.8fr) 78px 136px 100px 96px 58px 96px 100px 76px 72px'
  const shown = rows.slice(0, limit)
  return (
    <Screen title="Top clients" sub="Every client across every channel and tender, counter cash, Zelle and crypto included" wide
      right={<>
        <Seg label="Rank by" value={range} onChange={setRange} options={[['life', 'Lifetime'], ['l12', 'Last 12 months']]} />
        <Button onClick={() => d && clientsCsv(rows, `starchive-clients-${range}.csv`)} disabled={!d}>Export CSV</Button>
      </>}>
      {signer.askEl}
      {error && <div className="rp-error">{error}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Clients who have bought" value={t ? int(t.with_purchase) : '…'} sub={t ? `of ${int(t.clients)} named clients` : ''} />
        <Tile label="Top tier" value={t ? int(t.top) : '…'} sub={cfg ? `${money(cfg.top_cents / 100, true)}+ or ${cfg.top_purchases}+ purchases${cfg.months ? `, last ${cfg.months} months` : ', lifetime'}. Core: ${int(t?.core ?? 0)}` : ''} />
        <Tile label="Sales with no client" tone={t && t.no_client_pct > 5 ? 'red' : 'plain'} value={t ? `${t.no_client_pct}%` : '…'} sub={t ? `${money(t.no_client, true)} over ${int(t.no_client_n)} sales. Drive this to zero` : ''} />
        <Tile label="Lapsed Top and Core" tone={t && t.lapsed_top_core ? 'amber' : 'plain'} value={t ? int(t.lapsed_top_core) : '…'} sub={cfg ? `No purchase in ${cfg.lapsed_days}+ days` : ''} />
        <Tile label="Top and Core with no advisor" tone={t && t.no_advisor_top_core ? 'amber' : 'plain'} value={t ? int(t.no_advisor_top_core) : '…'} sub="Every Top and Core client gets one advisor" />
      </div>
      {p && <div className="cl-progress"><b>{int(p.shopify)}</b> Shopify customers · <b>{int(p.named)}</b> named · <b className={p.to_fill ? 'rp-tone-amber' : ''}>{int(p.to_fill)}</b> to fill in. A customer with no name stays out of this list until someone identifies them; their orders count under Unattached sales.</div>}
      <div className="rp-filterbar">
        <div className="cl-filters">
          {FILTERS.map(([k, l]) => <button key={k} type="button" className={'cl-chip' + (filter === k ? ' is-on' : '')} aria-pressed={filter === k} onClick={() => { setFilter(k); setLimit(100) }}>{l} <span>{d ? int(count(k)) : ''}</span></button>)}
        </div>
        <div className="cl-filters">
          <select className="rp-select cl-mini" value={advisor} onChange={(e) => setAdvisor(e.target.value)} aria-label="Advisor">
            <option value="">Any advisor</option><option value="none">No advisor</option>
            {(d?.advisors ?? []).map((a) => <option key={a.employee_id} value={a.employee_id}>{a.name}</option>)}
          </select>
          <input className="rp-input rp-search" placeholder="Search name, email, phone, city" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search clients" />
        </div>
      </div>
      <div className="rp-card">
        <div className="cl-tablehead"><SplitKey /><span className="rp-muted cl-small">{loading && !d ? '…' : `${int(rows.length)} clients. Click one to open the account`}</span></div>
        <div className="rp-table" role="table">
          <div className="rp-table__head" style={{ gridTemplateColumns: cols }} role="row">
            {['#', 'Client', 'Tier', 'Advisor', range === 'life' ? 'Lifetime' : 'Last 12 mo', range === 'life' ? 'Last 12 mo' : 'Lifetime', 'Bought', 'Channels', 'Last bought', 'Since', 'Consent'].map((h, i) => <span key={h} role="columnheader" className={[4, 5, 6, 9].includes(i) ? 'r' : ''}>{h}</span>)}
          </div>
          {loading && !d && [0, 1, 2, 3].map((i) => <div key={i} className="rp-table__row rp-skel" style={{ gridTemplateColumns: cols }}>{Array.from({ length: 11 }, (_, j) => <span key={j} />)}</div>)}
          {d && !rows.length && <div className="rp-table__empty">No clients match</div>}
          {shown.map((c, i) => (
            <div key={c.id} className="rp-table__row rp-table__row--link cl-row" style={{ gridTemplateColumns: cols }} role="row" tabIndex={0}
              onClick={() => navigate('/clients/c/' + c.id)} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/clients/c/' + c.id) }}>
              <span className="rp-muted">{i + 1}</span>
              <span className="cl-who"><b>{c.name}</b><small>{[c.city, c.seller ? 'Sells to us' : '', c.consignor ? 'Consignor' : ''].filter(Boolean).join(' · ') || 'No city on file'}</small></span>
              <span><TierChip tier={c.tier} pinned={c.pinned} /></span>
              <span><AdvisorPick row={c} advisors={d?.advisors ?? []} onSaved={refresh} signer={signer} /></span>
              <span className="r"><b>{money(range === 'life' ? c.life : c.l12, true)}</b></span>
              <span className="r rp-muted">{money(range === 'life' ? c.l12 : c.life, true)}</span>
              <span className="r">{int(c.purchases)}</span>
              <span><SplitBar split={c.split} /></span>
              <span>{dateY(c.last)}</span>
              <span className={'r' + (c.lapsed ? ' rp-tone-red' : '')}>{daysLabel(c.days)}</span>
              <span className="cl-dots" title="Email marketing · SMS marketing"><Dot on={c.email_ok} label="Email marketing" /><Dot on={c.sms_ok} label="SMS marketing" /></span>
            </div>
          ))}
        </div>
        {rows.length > shown.length && <div className="cl-more"><Button small onClick={() => setLimit(limit + 200)}>Show {int(Math.min(200, rows.length - shown.length))} more</Button></div>}
      </div>
    </Screen>
  )
}
